import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { Wrench, Paperclip, Trash, ArrowClockwise, CheckCircle, XCircle, CurrencyInr } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { NpdProjectPicker } from "@/components/NpdProjectPicker";
import { NpdTaskPicker } from "@/components/NpdTaskPicker";
import { projectCustomerName } from "@/lib/projectCustomer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  pending_head: { label: "Awaiting NPD Head", tone: "bg-[#FFFAEB] text-[#B54708] border-[#FEDF89]" },
  pending_management: { label: "Awaiting Management", tone: "bg-[#EFF8FF] text-[#175CD3] border-[#B2DDFF]" },
  approved: { label: "Approved", tone: "bg-[#ECFDF3] text-[#027A48] border-[#ABEFC6]" },
  rejected: { label: "Rejected", tone: "bg-[#FEF3F2] text-[#B42318] border-[#FECDCA]" },
};

const money = (v, currency = "INR") => (v == null ? "\u2014"
  : `${currency === "INR" ? "\u20B9" : `${currency} `}${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

const when = (v) => (v ? new Date(v).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "\u2014");

const DecisionLine = ({ title, decision, currency }) => {
  if (!decision) return null;
  const cut = decision.action === "approve" && decision.remark;
  return (
    <div className="text-[11px] text-[#475467]">
      <span className="font-bold">{title}:</span>{" "}
      {decision.action === "approve" ? `approved ${money(decision.amount_approved, currency)}` : "rejected"}
      {" by "}{decision.by}{" \u00B7 "}{when(decision.at)}
      {cut || decision.action === "reject" ? <span className="block text-[#B54708]">&ldquo;{decision.remark}&rdquo;</span> : null}
    </div>
  );
};

export const NpdToolingApprovalTab = ({ mode = "requests" }) => {
  const isApprovals = mode === "approvals";
  const [rows, setRows] = useState([]);
  const [rights, setRights] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  // Jun 2026, user's ask: Approvals is its own menu item, so it opens on the
  // ones actually waiting for THIS approver rather than the whole history.
  const [onlyMine, setOnlyMine] = useState(true);

  const [projectId, setProjectId] = useState("");
  const [projectName, setProjectName] = useState("");
  const [taskId, setTaskId] = useState("");
  const [taskName, setTaskName] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [amount, setAmount] = useState("");
  const [remark, setRemark] = useState("");
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInput = useRef(null);

  const [decisionTarget, setDecisionTarget] = useState(null);
  const [decisionAction, setDecisionAction] = useState("approve");
  const [decisionAmount, setDecisionAmount] = useState("");
  const [decisionRemark, setDecisionRemark] = useState("");
  const [deciding, setDeciding] = useState(false);
  const [postingId, setPostingId] = useState("");

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (search.trim()) params.search = search.trim();
      const { data } = await axios.get(`${API}/npd-tooling/requests`, { params });
      setRows(data.requests || []);
      setRights(data.rights || {});
    } catch (e) {
      if (!silent) toast.error(e.response?.data?.detail || "Could not load tooling budget requests");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);

  useEffect(() => {
    const t = setTimeout(() => load(), 300);
    return () => clearTimeout(t);
  }, [load]);

  const addFiles = async (picked) => {
    if (!picked.length) return;
    setUploading(true);
    for (const file of picked) {
      const form = new FormData();
      form.append("file", file);
      try {
        const { data } = await axios.post(`${API}/npd-tooling/attachments`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setFiles((prev) => [...prev, data]);
      } catch (e) {
        toast.error(e.response?.data?.detail || `Could not upload ${file.name}`);
      }
    }
    setUploading(false);
    if (fileInput.current) fileInput.current.value = "";
  };

  const missingField = useMemo(() => {
    if (!projectId) return "Pick the SAP project this tooling is for";
    if (!taskId) return "Pick the project task the budget belongs to";
    if (!(Number(amount) > 0)) return "Enter the tooling budget amount";
    if (remark.trim().length < 5) return "Say what this tooling is for";
    if (files.length === 0) return "Attach the quotation or costing sheet";
    return "";
  }, [projectId, taskId, amount, remark, files]);

  const submit = async () => {
    if (missingField) return toast.error(missingField);
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/npd-tooling/requests`, {
        project_id: projectId, project_name: projectName, task_id: taskId, task_name: taskName,
        customer_name: customerName,
        amount: Number(amount), remark: remark.trim(),
        attachment_ids: files.map((f) => f.file_id),
      });
      toast.success(`${data._id} sent to the NPD head for approval`);
      setProjectId(""); setProjectName(""); setTaskId(""); setTaskName("");
      setCustomerName(""); setAmount(""); setRemark(""); setFiles([]);
      load(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not raise this tooling budget request");
    } finally {
      setSubmitting(false);
    }
  };

  const canDecide = (r) => isApprovals && ((r.status === "pending_head" && rights.can_approve_head)
    || (r.status === "pending_management" && rights.can_approve_management));

  const waitingOnMe = (r) => (r.status === "pending_head" && rights.can_approve_head)
    || (r.status === "pending_management" && rights.can_approve_management);

  const visibleRows = useMemo(
    () => (isApprovals && onlyMine ? rows.filter(waitingOnMe) : rows),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, isApprovals, onlyMine, rights],
  );

  const openDecision = (r, action) => {
    setDecisionTarget(r);
    setDecisionAction(action);
    setDecisionAmount(String(r.pending_amount ?? r.amount_requested ?? ""));
    setDecisionRemark("");
  };

  const sendDecision = async () => {
    setDeciding(true);
    try {
      const { data } = await axios.post(`${API}/npd-tooling/requests/${decisionTarget._id}/decision`, {
        action: decisionAction,
        amount: decisionAction === "approve" ? Number(decisionAmount) : null,
        remark: decisionRemark.trim(),
      });
      toast.success(
        data.status === "approved"
          ? `${data._id} approved - budget ${money(data.approved_amount, data.currency)}`
          : data.status === "pending_management" ? `${data._id} passed to management`
          : `${data._id} rejected`,
        data.status === "approved"
          ? {
            description: data.sap_budget?.status === "posted"
              ? `Written to SAP as planned cost on task ${data.sap_budget.task_id}`
              : `SAP did not take the budget yet - use "Send budget to SAP" on the request`,
          }
          : undefined,
      );
      setDecisionTarget(null);
      load(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not record that decision");
    } finally {
      setDeciding(false);
    }
  };

  const postBudget = async (r) => {
    setPostingId(r._id);
    try {
      const { data } = await axios.post(`${API}/npd-tooling/requests/${r._id}/post-budget`);
      if (data.sap_budget?.status === "posted") {
        toast.success(`${r._id}: budget written onto task ${data.sap_budget.task_id} in SAP`);
      } else {
        toast.error(data.sap_budget?.error || "SAP still refused the budget line");
      }
      load(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send the budget to SAP");
    } finally {
      setPostingId("");
    }
  };

  return (
    <div className="space-y-5" data-testid="npd-tooling-tab">
      {rights.can_raise && !isApprovals && (
        <div className="bg-white border border-[#D0D5DD] rounded-sm p-4 space-y-4" data-testid="npd-tooling-new-card">
          <div className="flex items-center gap-2">
            <Wrench size={18} weight="fill" className="text-[#0E7C86]" />
            <h2 className="font-heading text-base font-bold uppercase tracking-wide">Request Tooling Budget</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-xs font-bold text-[#344054]">SAP Project *</Label>
              <NpdProjectPicker
                value={projectId}
                name={projectName}
                onPick={(p) => {
                  setProjectId(p.project_id); setProjectName(p.name); setTaskId(""); setTaskName("");
                  setCustomerName(projectCustomerName(p, customerName));
                }}
                testIdPrefix="npd-tooling-project"
              />
              <p className="text-[10px] text-[#667085]">Live from SAP - the same project list as a store request.</p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-bold text-[#344054]">Project Task *</Label>
              <NpdTaskPicker
                projectId={projectId}
                value={taskId}
                onPick={(t) => { setTaskId(t.task_id); setTaskName(t.name || ""); }}
                testIdPrefix="npd-tooling-task"
              />
              <p className="text-[10px] text-[#667085]">
                The approved budget is written onto this task's expense plan in SAP.
              </p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-bold text-[#344054]">Tooling Budget (&#8377;) *</Label>
              <div className="relative">
                <CurrencyInr size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#667085]" />
                <Input
                  type="number" min="0" step="0.01" value={amount}
                  onChange={(e) => setAmount(e.target.value)} placeholder="0.00"
                  className="h-9 pl-7" data-testid="npd-tooling-amount-input"
                />
              </div>
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs font-bold text-[#344054]">What is this tooling for? *</Label>
            <Textarea
              value={remark} onChange={(e) => setRemark(e.target.value)} rows={2}
              placeholder="Die / fixture / mould, the part it makes and why it is needed now"
              data-testid="npd-tooling-remark-input"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-bold text-[#344054]">Quotations &amp; costing (at least one) *</Label>
            <div className="flex items-center gap-2 flex-wrap">
              <input
                ref={fileInput} type="file" multiple className="hidden"
                onChange={(e) => addFiles(Array.from(e.target.files || []))}
                data-testid="npd-tooling-file-input"
              />
              <Button
                type="button" variant="outline" size="sm" disabled={uploading}
                onClick={() => fileInput.current?.click()} data-testid="npd-tooling-add-file-button"
              >
                <Paperclip size={14} className="mr-1.5" /> {uploading ? "Uploading..." : "Add file"}
              </Button>
              <span className="text-[11px] text-[#667085]">PDF, image, Word or Excel &middot; up to 15 MB each</span>
            </div>
            {files.length > 0 && (
              <ul className="space-y-1" data-testid="npd-tooling-file-list">
                {files.map((f) => (
                  <li key={f.file_id} className="flex items-center gap-2 text-[12px] text-[#344054]">
                    <Paperclip size={12} className="text-[#667085]" />
                    <span className="truncate max-w-[340px]">{f.name}</span>
                    <span className="text-[10px] text-[#98A2B3]">{(f.size / 1024).toFixed(0)} KB</span>
                    <button
                      type="button" className="text-[#B42318] hover:underline"
                      onClick={() => setFiles((prev) => prev.filter((x) => x.file_id !== f.file_id))}
                      data-testid={`npd-tooling-remove-file-${f.file_id}`}
                    >
                      <Trash size={12} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex items-center gap-3">
            <Button
              onClick={submit} disabled={!!missingField || submitting || uploading}
              className="bg-[#0E7C86] hover:bg-[#0B656D]" data-testid="npd-tooling-submit-button"
            >
              {submitting ? "Sending..." : "Send for Approval"}
            </Button>
            {missingField && <span className="text-[11px] text-[#B54708]" data-testid="npd-tooling-missing-hint">{missingField}</span>}
          </div>
          <p className="text-[11px] text-[#667085]">
            Approval runs <strong>NPD Head</strong> first, then <strong>Management</strong>. Either can sanction a
            lower amount with a reason. Once management approves, the sanctioned amount is written into SAP as
            planned cost on the chosen project task, so it shows up in the project's expenditure.
          </p>
        </div>
      )}

      <div className="bg-white border border-[#D0D5DD] rounded-sm" data-testid="npd-tooling-list-card">
        <div className="p-3 border-b border-[#EAECF0] flex items-center gap-2 flex-wrap">
          <h3 className="font-heading text-sm font-bold uppercase tracking-wide">
            {isApprovals ? "Tooling Budgets to Decide" : "My Tooling Budget Requests"}
          </h3>
          {isApprovals && (
            <label className="flex items-center gap-1.5 text-[11px] text-[#344054] cursor-pointer">
              <input
                type="checkbox" checked={onlyMine} onChange={(e) => setOnlyMine(e.target.checked)}
                data-testid="npd-tooling-only-mine"
              />
              Waiting on me
            </label>
          )}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-8 w-[190px] bg-white text-xs" data-testid="npd-tooling-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending_head">Awaiting NPD Head</SelectItem>
              <SelectItem value="pending_management">Awaiting Management</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
          <Input
            value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Request, project or person"
            className="h-8 w-[240px] text-xs" data-testid="npd-tooling-search-input"
          />
          <Button variant="outline" size="sm" onClick={() => load(true)} className="ml-auto" data-testid="npd-tooling-refresh-button">
            <ArrowClockwise size={14} className="mr-1.5" /> Refresh
          </Button>
          <a
            href="/docs/tooling-budget-flow" target="_blank" rel="noreferrer"
            className="text-[11px] text-[#0E7C86] hover:underline font-bold"
            data-testid="npd-tooling-guide-link"
          >
            How it works
          </a>
          <span className="text-[11px] text-[#667085]" data-testid="npd-tooling-count">{visibleRows.length} request(s)</span>
        </div>

        <div className="divide-y divide-[#EAECF0]">
          {visibleRows.length === 0 && (
            <p className="p-6 text-center text-sm text-[#98A2B3]" data-testid="npd-tooling-empty-state">
              {loading ? "Loading tooling budget requests\u2026"
                : isApprovals && onlyMine ? "Nothing is waiting for your approval right now."
                : "No tooling budget requests match this filter."}
            </p>
          )}
          {visibleRows.map((r) => {
            const badge = STATUS_BADGE[r.status] || { label: r.status, tone: "bg-[#F2F4F7] text-[#475467] border-[#EAECF0]" };
            return (
              <div key={r._id} className="p-3 space-y-2" data-testid={`npd-tooling-row-${r._id}`}>
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="min-w-[200px]">
                    <div className="font-mono text-sm font-bold">{r._id}</div>
                    <div className="text-[11px] text-[#667085]">{r.requester} &middot; {when(r.created_at)}</div>
                  </div>
                  <div className="min-w-[240px] flex-1">
                    <div className="text-[13px] font-bold text-[#344054]">{r.project_id}{r.task_id ? <span className="text-[#667085] font-normal"> &rarr; {r.task_id}</span> : null}</div>
                    <div className="text-[11px] text-[#667085]">{r.project_name}{r.customer_name ? ` \u00B7 ${r.customer_name}` : ""}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[11px] text-[#667085]">Requested</div>
                    <div className="text-sm font-bold" data-testid={`npd-tooling-requested-${r._id}`}>{money(r.amount_requested, r.currency)}</div>
                  </div>
                  {r.status === "approved" && (
                    <div className="text-right">
                      <div className="text-[11px] text-[#667085]">Sanctioned</div>
                      <div className="text-sm font-bold text-[#027A48]" data-testid={`npd-tooling-approved-${r._id}`}>{money(r.approved_amount, r.currency)}</div>
                    </div>
                  )}
                  <Badge variant="outline" className={`${badge.tone} text-[11px]`} data-testid={`npd-tooling-status-${r._id}`}>{badge.label}</Badge>
                </div>

                <p className="text-[12px] text-[#475467]">{r.remark}</p>

                <div className="flex items-center gap-3 flex-wrap">
                  {(r.attachments || []).map((a) => (
                    <a
                      key={a.file_id}
                      href={`${API}/npd-tooling/requests/${r._id}/attachments/${a.file_id}`}
                      target="_blank" rel="noreferrer"
                      className="flex items-center gap-1 text-[12px] text-[#0E7C86] hover:underline"
                      data-testid={`npd-tooling-attachment-${a.file_id}`}
                    >
                      <Paperclip size={12} /> {a.name}
                    </a>
                  ))}
                </div>

                <DecisionLine title="NPD Head" decision={r.head_decision} currency={r.currency} />
                <DecisionLine title="Management" decision={r.management_decision} currency={r.currency} />

                {r.status === "approved" && (
                  <div className="flex items-center gap-2 flex-wrap text-[11px]" data-testid={`npd-tooling-sap-${r._id}`}>
                    {r.sap_budget?.status === "posted" ? (
                      <span className="text-[#027A48]">
                        In SAP: {money(r.sap_budget.planned_amount, r.sap_budget.currency)} planned on task{" "}
                        <strong>{r.sap_budget.task_id}</strong> under &ldquo;{r.sap_budget.expense_group_text}&rdquo;
                      </span>
                    ) : (
                      <>
                        <span className="text-[#B42318]">
                          Not yet in SAP{r.sap_budget?.error ? `: ${r.sap_budget.error}` : ""}
                        </span>
                        {rights.can_approve_management && isApprovals && (
                          <Button
                            size="sm" variant="outline" className="h-7 text-[11px]"
                            onClick={() => postBudget(r)} disabled={postingId === r._id}
                            data-testid={`npd-tooling-post-budget-${r._id}`}
                          >
                            {postingId === r._id ? "Sending..." : "Send budget to SAP"}
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                )}

                {canDecide(r) && (
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm" className="bg-[#027A48] hover:bg-[#026234] h-8"
                      onClick={() => openDecision(r, "approve")} data-testid={`npd-tooling-approve-${r._id}`}
                    >
                      <CheckCircle size={14} className="mr-1.5" /> Approve
                    </Button>
                    <Button
                      size="sm" variant="outline" className="h-8 text-[#B42318] border-[#FECDCA]"
                      onClick={() => openDecision(r, "reject")} data-testid={`npd-tooling-reject-${r._id}`}
                    >
                      <XCircle size={14} className="mr-1.5" /> Reject
                    </Button>
                    <span className="text-[11px] text-[#667085]">You are the {r.stage_label} approver &middot; up to {money(r.pending_amount, r.currency)}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <Dialog open={!!decisionTarget} onOpenChange={(o) => !o && setDecisionTarget(null)}>
        <DialogContent className="bg-white" data-testid="npd-tooling-decision-dialog">
          <DialogHeader>
            <DialogTitle>
              {decisionAction === "approve" ? "Approve" : "Reject"} {decisionTarget?._id} &middot; {decisionTarget?.stage_label}
            </DialogTitle>
          </DialogHeader>
          {decisionTarget && (
            <div className="space-y-3">
              <p className="text-[12px] text-[#475467]">
                {decisionTarget.project_id} {decisionTarget.project_name} &middot; asked for{" "}
                <strong>{money(decisionTarget.pending_amount, decisionTarget.currency)}</strong>
              </p>
              {decisionAction === "approve" && (
                <div className="space-y-1">
                  <Label className="text-xs font-bold text-[#344054]">Amount you sanction (&#8377;)</Label>
                  <Input
                    type="number" min="0" step="0.01" value={decisionAmount}
                    onChange={(e) => setDecisionAmount(e.target.value)} className="h-9"
                    data-testid="npd-tooling-decision-amount"
                  />
                  <p className="text-[10px] text-[#667085]">Lower it if only part of the budget is sanctioned - then a reason is required.</p>
                </div>
              )}
              <div className="space-y-1">
                <Label className="text-xs font-bold text-[#344054]">
                  Remark {decisionAction === "reject" || Number(decisionAmount) < Number(decisionTarget.pending_amount) ? "*" : "(optional)"}
                </Label>
                <Textarea
                  value={decisionRemark} onChange={(e) => setDecisionRemark(e.target.value)} rows={2}
                  placeholder={decisionAction === "reject" ? "Why this is rejected" : "Why the amount was changed"}
                  data-testid="npd-tooling-decision-remark"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecisionTarget(null)} data-testid="npd-tooling-decision-cancel">Cancel</Button>
            <Button
              onClick={sendDecision} disabled={deciding}
              className={decisionAction === "approve" ? "bg-[#027A48] hover:bg-[#026234]" : "bg-[#B42318] hover:bg-[#912018]"}
              data-testid="npd-tooling-decision-confirm"
            >
              {deciding ? "Saving..." : decisionAction === "approve" ? "Approve" : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
